import type { ReactNode } from "react";
import type { Character } from "@spieleparadies/shared";

interface CharacterAvatarProps {
  character: Character;
  size?: number;
}

/**
 * Inline-SVG-Platzhalter pro Charakter-Style. Sobald echte Sprites unter
 * `public/assets/characters/<id>.png` liegen, kann man hier den `<img>`
 * statt der SVGs zeigen.
 */
export function CharacterAvatar({ character, size = 64 }: CharacterAvatarProps) {
  switch (character.style) {
    case "stitch":
      return <StitchAvatar character={character} size={size} />;
    case "lion":
      return <LionAvatar character={character} size={size} />;
    case "snowman":
      return <SnowmanAvatar character={character} size={size} />;
    case "mouse":
      return <MouseAvatar character={character} size={size} />;
    case "genie":
      return <GenieAvatar character={character} size={size} />;
    case "spaceman":
      return <SpacemanAvatar character={character} size={size} />;
    case "human":
    default:
      return <HumanAvatar character={character} size={size} />;
  }
}

function Frame({ children, size, label }: { children: ReactNode; size: number; label: string }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-label={label}>
      <rect width="64" height="64" rx="14" fill="#0b0d17" />
      {children}
    </svg>
  );
}

function HumanAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      <defs>
        <linearGradient id={`g-${character.id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={character.color} />
          <stop offset="1" stopColor={character.accent} />
        </linearGradient>
      </defs>
      <rect x="18" y="14" width="28" height="40" rx="8" fill={`url(#g-${character.id})`} />
      <circle cx="26" cy="26" r="3" fill="#fff" />
      <circle cx="38" cy="26" r="3" fill="#fff" />
      <circle cx="26" cy="26" r="1.4" fill="#0b0d17" />
      <circle cx="38" cy="26" r="1.4" fill="#0b0d17" />
      <rect x="28" y="38" width="8" height="2" rx="1" fill="#0b0d17" />
    </Frame>
  );
}

function StitchAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      <ellipse cx="16" cy="22" rx="6" ry="10" fill={character.color} />
      <ellipse cx="48" cy="22" rx="6" ry="10" fill={character.color} />
      <ellipse cx="16" cy="24" rx="3" ry="6" fill={character.accent} />
      <ellipse cx="48" cy="24" rx="3" ry="6" fill={character.accent} />
      <ellipse cx="32" cy="34" rx="16" ry="14" fill={character.color} />
      <ellipse cx="32" cy="40" rx="10" ry="7" fill={character.accent} opacity="0.4" />
      <circle cx="26" cy="32" r="3.2" fill="#0b0d17" />
      <circle cx="38" cy="32" r="3.2" fill="#0b0d17" />
      <circle cx="27" cy="31" r="1" fill="#fff" />
      <circle cx="39" cy="31" r="1" fill="#fff" />
      <ellipse cx="32" cy="37" rx="2" ry="1.4" fill="#0b0d17" />
      <path d="M28 41 Q32 44 36 41" stroke="#0b0d17" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    </Frame>
  );
}

function LionAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      {/* Mähne */}
      <circle cx="32" cy="32" r="20" fill={character.accent} />
      {/* Kopf */}
      <ellipse cx="32" cy="34" rx="14" ry="12" fill={character.color} />
      {/* Ohren */}
      <circle cx="20" cy="22" r="4" fill={character.accent} />
      <circle cx="44" cy="22" r="4" fill={character.accent} />
      <circle cx="20" cy="22" r="2" fill={character.color} />
      <circle cx="44" cy="22" r="2" fill={character.color} />
      {/* Schnauze */}
      <ellipse cx="32" cy="40" rx="8" ry="5" fill="#fde68a" />
      {/* Augen */}
      <circle cx="27" cy="32" r="2" fill="#0b0d17" />
      <circle cx="37" cy="32" r="2" fill="#0b0d17" />
      {/* Nase */}
      <path d="M30 37 L34 37 L32 39 Z" fill="#0b0d17" />
      <path d="M32 39 L32 41" stroke="#0b0d17" strokeWidth="1.2" />
      <path d="M28 42 Q32 44 36 42" stroke="#0b0d17" strokeWidth="1.2" fill="none" />
    </Frame>
  );
}

function SnowmanAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      {/* Schneeballkörper */}
      <circle cx="32" cy="46" r="10" fill={character.color} />
      <circle cx="32" cy="32" r="9" fill={character.color} />
      <circle cx="32" cy="20" r="7" fill={character.color} />
      {/* Augen + Mund (Kohle) */}
      <circle cx="29" cy="19" r="1.2" fill="#0b0d17" />
      <circle cx="35" cy="19" r="1.2" fill="#0b0d17" />
      <circle cx="29" cy="24" r="0.8" fill="#0b0d17" />
      <circle cx="32" cy="24.5" r="0.8" fill="#0b0d17" />
      <circle cx="35" cy="24" r="0.8" fill="#0b0d17" />
      {/* Karottennase */}
      <polygon points="32,21 41,22 32,23" fill={character.accent} />
      {/* Ast-Arme */}
      <line x1="22" y1="32" x2="15" y2="28" stroke="#7c2d12" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="42" y1="32" x2="49" y2="28" stroke="#7c2d12" strokeWidth="1.5" strokeLinecap="round" />
      {/* Knöpfe */}
      <circle cx="32" cy="32" r="1" fill="#0b0d17" />
      <circle cx="32" cy="44" r="1.2" fill="#0b0d17" />
      <circle cx="32" cy="48" r="1.2" fill="#0b0d17" />
    </Frame>
  );
}

function MouseAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      {/* Ohren */}
      <circle cx="18" cy="18" r="8" fill={character.color} />
      <circle cx="46" cy="18" r="8" fill={character.color} />
      {/* Kopf */}
      <circle cx="32" cy="34" r="14" fill={character.color} />
      {/* Gesicht heller */}
      <ellipse cx="32" cy="40" rx="9" ry="7" fill="#fde68a" />
      {/* Augen */}
      <ellipse cx="27" cy="32" rx="2" ry="3" fill="#fff" />
      <ellipse cx="37" cy="32" rx="2" ry="3" fill="#fff" />
      <ellipse cx="27" cy="33" rx="1" ry="1.6" fill="#0b0d17" />
      <ellipse cx="37" cy="33" rx="1" ry="1.6" fill="#0b0d17" />
      {/* Nase */}
      <ellipse cx="32" cy="38" rx="2.5" ry="2" fill="#0b0d17" />
      {/* Mund */}
      <path d="M27 43 Q32 47 37 43" stroke="#0b0d17" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      {/* Hosenträger-Knöpfe */}
      <circle cx="26" cy="52" r="1.2" fill={character.accent} />
      <circle cx="38" cy="52" r="1.2" fill={character.accent} />
    </Frame>
  );
}

function GenieAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      {/* Rauchwolke unten */}
      <ellipse cx="32" cy="58" rx="22" ry="4" fill={character.color} opacity="0.3" />
      <path d="M14 56 Q18 48 16 40 Q12 36 16 32" stroke={character.color} strokeWidth="2" fill="none" opacity="0.5" />
      {/* Körper */}
      <path d="M22 56 Q22 38 32 30 Q42 38 42 56 Z" fill={character.color} />
      {/* Kopf */}
      <circle cx="32" cy="26" r="10" fill={character.color} />
      {/* Turban */}
      <path d="M22 22 Q32 14 42 22 Q42 16 32 14 Q22 16 22 22" fill={character.accent} />
      <circle cx="32" cy="16" r="1.5" fill="#7f1d1d" />
      {/* Augen */}
      <circle cx="28" cy="26" r="1.4" fill="#0b0d17" />
      <circle cx="36" cy="26" r="1.4" fill="#0b0d17" />
      {/* Bart */}
      <path d="M28 30 Q32 36 36 30" stroke="#0b0d17" strokeWidth="1.5" fill="none" />
      {/* Ohrring */}
      <circle cx="42" cy="28" r="1.4" fill={character.accent} />
    </Frame>
  );
}

function SpacemanAvatar({ character, size }: { character: Character; size: number }) {
  return (
    <Frame size={size} label={character.name}>
      {/* Helm */}
      <rect x="14" y="14" width="36" height="34" rx="14" fill={character.color} />
      {/* Visier */}
      <rect x="20" y="22" width="24" height="14" rx="6" fill="#1e1b4b" />
      <rect x="22" y="24" width="8" height="4" rx="2" fill="#60a5fa" opacity="0.7" />
      {/* Antennen */}
      <line x1="22" y1="14" x2="20" y2="8" stroke={character.accent} strokeWidth="1.5" />
      <circle cx="20" cy="7" r="1.5" fill={character.accent} />
      {/* Brust mit Knöpfen */}
      <rect x="22" y="40" width="20" height="14" rx="3" fill={character.color} />
      <circle cx="26" cy="47" r="1.4" fill={character.accent} />
      <circle cx="32" cy="47" r="1.4" fill="#ef4444" />
      <circle cx="38" cy="47" r="1.4" fill={character.accent} />
      {/* Streifen */}
      <rect x="22" y="50" width="20" height="2" fill={character.accent} />
    </Frame>
  );
}
